import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  BrandSymbol,
  Button,
  Container,
  Divider,
  Group,
  Icon,
  Loader,
  SectionBannerCloseButton,
  SectionBannerContent,
  SectionBannerDescription,
  SectionBannerIcon,
  SectionBannerRoot,
  SectionBannerTitle,
  Text,
  useBreakpointValue,
} from '@melio/penny';

import { getGrantInteraction, getSession, grantScopes, GrantInteraction, ScopeWithDescription } from '../../api/auth';
import { useAnalytics } from '../../analytics';
import { Logger } from '../../monitoring';
import { sanitizeRedirectParam } from '../../utils/sanitizeRedirectUrl';

const PAGE_NAME = 'consent';
const FLOW = 'consent';

type LoadState = { type: 'loading' } | { type: 'ready'; email: string; interaction: GrantInteraction } | { type: 'invalid' };

// Final product copy. Kept inline: this app has no translations layer, and
// per review the consent screen should not introduce one.
const COPY = {
  titleSuffix: 'wants access to your Melio account',
  cardTitle: 'This will authorize the app to',
  alreadyGrantedTitle: 'This app already has permissions to',
  signedInAs: 'Signed in as',
  cancel: 'Cancel',
  authorize: 'Authorize',
  errorGeneric: 'Something went wrong. Please try again.',
  invalidLinkTitle: 'This link is invalid or has expired',
  invalidLinkDescription: 'Return to the app you were connecting and start the authorization again.',
};

// The dashed connector line between the app logo and the Melio mark; matches
// the design's 61.56x1 line (stroke #C3CAD5, 4-4 dashes), which Penny has no
// primitive for.
const ConnectorLine = () => (
  <svg width="61.56" height="1" viewBox="0 0 61.56 1" fill="none" aria-hidden>
    <line x1="0" y1="0.5" x2="61.56" y2="0.5" stroke="#C3CAD5" strokeDasharray="4 4" />
  </svg>
);

const ScopeList = ({ scopes, testId }: { scopes: ScopeWithDescription[]; testId: string }) => (
  <Group variant="vertical" spacing="s" data-testid={testId}>
    {scopes.map(({ scope, description }) => (
      <Group key={scope} variant="horizontal" spacing="xxs" alignItems="center" data-testid={`${testId}-${scope}`}>
        <Text as="span" color="semantic.text.secondary" aria-hidden>
          <Icon type="checked-mini" size="small" color="inherit" />
        </Text>
        <Text textStyle="body3" color="semantic.text.secondary">
          {description}
        </Text>
      </Group>
    ))}
  </Group>
);

export const GrantPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useBreakpointValue({ xs: true, s: false });
  const { track } = useAnalytics();
  const [searchParams] = useSearchParams();

  const interactionId = searchParams.get('interactionId') ?? '';

  const [loadState, setLoadState] = useState<LoadState>({ type: 'loading' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showError, setShowError] = useState(false);

  document.title = 'Authorize access';

  // Bounce to sign-in with this /grant URL as the (same-origin) redirect back,
  // so login returns here with the original consent context intact.
  const goToSignIn = () => {
    const returnTo = `${location.pathname}${location.search}`;
    const search = new URLSearchParams({ redirect: returnTo }).toString();
    navigate({ pathname: '/sign-in', search }, { replace: true });
  };

  // Resolve everything the page needs: the session (an expired one bounces to
  // sign-in, not the invalid-link state) and then the interaction itself.
  const resolveLoadState = async (): Promise<LoadState | 'signIn'> => {
    const session = await getSession();
    if (session.type === 'unauthenticated') {
      return 'signIn';
    }

    const result = await getGrantInteraction(interactionId);
    switch (result.type) {
      case 'ready':
        return { type: 'ready', email: session.user.user.email, interaction: result.interaction };
      case 'sessionExpired':
        return 'signIn';
      case 'invalid':
        return { type: 'invalid' };
      case 'error':
        Logger.error('Get grant interaction failed', { message: result.message });
        return { type: 'invalid' };
      default:
        result satisfies never;
        return { type: 'invalid' };
    }
  };

  useEffect(() => {
    if (!interactionId) {
      setLoadState({ type: 'invalid' });
      return;
    }

    let cancelled = false;
    void resolveLoadState().then((next) => {
      if (cancelled) {
        return;
      }
      if (next === 'signIn') {
        goToSignIn();
      } else {
        setLoadState(next);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [interactionId]);

  useEffect(() => {
    if (loadState.type === 'ready') {
      track({ eventName: 'Connection-View', properties: { PageName: PAGE_NAME, Flow: FLOW, Intent: 'consent', EntryPoint: 'sign-in' } });
    }
  }, [loadState.type, track]);

  const trackClick = (cta: 'allow-access' | 'cancel') => {
    track({
      eventName: 'Connection-Click',
      properties: { PageName: PAGE_NAME, Flow: FLOW, Intent: 'consent', EntryPoint: 'sign-in', Cta: cta },
    });
  };

  const trackStatus = (statusType: 'success' | 'failure') => {
    track({
      eventName: 'Connection-Status',
      properties: {
        PageName: PAGE_NAME,
        Flow: FLOW,
        Intent: 'consent',
        EntryPoint: 'sign-in',
        Cta: 'allow-access',
        StatusType: statusType,
      },
    });
  };

  // The authorize URL that resumes the OAuth flow. Server-provided, but still
  // sanitized to the current origin before any navigation.
  const getSafeRedirect = (interaction: GrantInteraction) => sanitizeRedirectParam(interaction.redirect);

  const handleApprove = async (interaction: GrantInteraction) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setShowError(false);
    trackClick('allow-access');

    const result = await grantScopes({ interactionId });

    switch (result.type) {
      case 'success': {
        trackStatus('success');
        const redirect = getSafeRedirect(interaction);
        if (redirect) {
          window.location.assign(redirect);
        } else {
          Logger.error('Grant redirect rejected by sanitizer');
          setLoadState({ type: 'invalid' });
        }
        return;
      }
      case 'sessionExpired':
        goToSignIn();
        return;
      case 'invalidInteraction': {
        // Expired/used while the screen was open: resume the authorize flow,
        // which mints a fresh interaction (or issues a code if already granted).
        const redirect = getSafeRedirect(interaction);
        if (redirect) {
          window.location.assign(redirect);
        } else {
          setLoadState({ type: 'invalid' });
        }
        return;
      }
      case 'error':
        Logger.error('Grant scopes failed', { message: result.message });
        trackStatus('failure');
        setShowError(true);
        setIsSubmitting(false);
        return;
      default:
        result satisfies never;
    }
  };

  const handleDeny = (interaction: GrantInteraction) => {
    trackClick('cancel');
    const redirect = getSafeRedirect(interaction);
    if (!redirect) {
      setLoadState({ type: 'invalid' });
      return;
    }
    // Backend forwards the error to the client's redirect_uri. Set it
    // structurally so it survives any redirect shape.
    const url = new URL(redirect, window.location.origin);
    url.searchParams.set('error', 'access_denied');
    window.location.assign(`${url.pathname}${url.search}`);
  };

  if (loadState.type === 'invalid') {
    return (
      <Container
        data-testid="grant-page"
        backgroundColor="light"
        height="full"
        paddingX={isMobile ? 's' : 'xl'}
        paddingY={isMobile ? 's' : 'l'}
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: '100vh' }}
      >
        <Container width="full" maxWidth="480px" data-testid="grant-invalid-link">
          <SectionBannerRoot variant="critical">
            <SectionBannerIcon />
            <SectionBannerContent>
              <SectionBannerTitle>{COPY.invalidLinkTitle}</SectionBannerTitle>
              <SectionBannerDescription>{COPY.invalidLinkDescription}</SectionBannerDescription>
            </SectionBannerContent>
          </SectionBannerRoot>
        </Container>
      </Container>
    );
  }

  if (loadState.type === 'loading') {
    return (
      <Container data-testid="grant-page" height="full" alignItems="center" justifyContent="center" style={{ minHeight: '100vh' }}>
        <Loader />
      </Container>
    );
  }

  const { interaction } = loadState;

  return (
    <Container
      data-testid="grant-page"
      backgroundColor="light"
      height="full"
      paddingX={isMobile ? 's' : 'l'}
      paddingY="xxl"
      alignItems="center"
      justifyContent="flex-start"
      style={{ minHeight: '100vh' }}
    >
      <Container overflow="visible" width="full" alignItems="center" justifyContent="center">
        <Container overflow="visible" width="full" maxWidth="480px">
          <Group variant="vertical" spacing="m" alignItems="stretch">
            <Group variant="horizontal" spacing="xs" alignItems="center" justifyContent="center" data-testid="grant-brand">
              {interaction.logoUrl ? (
                <img src={interaction.logoUrl} alt={interaction.displayName} width={54} height={54} />
              ) : (
                <Icon type="link" size="large" aria-hidden />
              )}
              <ConnectorLine />
              <Text as="span" color="semantic.text.secondary" aria-hidden>
                <Icon type="link-circle-outline" size="large" color="inherit" />
              </Text>
              <ConnectorLine />
              <BrandSymbol type="melio-secondary" size="medium" width={53} height={57} aria-label="Melio" />
            </Group>

            <Container textAlign="center">
              <Text textStyle="body2" data-testid="grant-page-title">
                <Text as="span" textStyle="body2Semi">
                  {interaction.displayName}
                </Text>{' '}
                {COPY.titleSuffix}
              </Text>
            </Container>

            <Container border="regular" backgroundColor="white" paddingX="l" paddingY="l" style={{ borderRadius: '16px' }}>
              <Group variant="vertical" spacing="m">
                <Group variant="horizontal" spacing="xs" alignItems="center">
                  <Icon type="shield-check" size="small" aria-hidden />
                  <Text textStyle="body3Semi" data-testid="grant-access-title">
                    {COPY.cardTitle}
                  </Text>
                </Group>

                <ScopeList scopes={interaction.missingScopes} testId="grant-missing-scopes" />

                <SectionBannerRoot variant="brand" data-testid="grant-signed-in-banner">
                  {/* The design shows a neutral info mark, not the brand variant's built-in
                      "?" status icon; centered vertically against a wrapping email. */}
                  <Container width="fit-content" style={{ alignSelf: 'center' }} aria-hidden>
                    {/* inherit: Icon's own brand color prop doesn't render in this app */}
                    <Text as="span" color="semantic.text.brand.rest">
                      <Icon type="info-fill" size="small" color="inherit" />
                    </Text>
                  </Container>
                  <SectionBannerContent>
                    <Text textStyle="body4">
                      {COPY.signedInAs}{' '}
                      <Text as="span" textStyle="body4Semi" data-testid="grant-signed-in-as">
                        {loadState.email}
                      </Text>
                    </Text>
                  </SectionBannerContent>
                </SectionBannerRoot>

                {interaction.grantedScopes.length > 0 && (
                  <>
                    <Divider />
                    <Group variant="vertical" spacing="m" data-testid="grant-already-granted">
                      <Text textStyle="body3Semi">{COPY.alreadyGrantedTitle}</Text>
                      <ScopeList scopes={interaction.grantedScopes} testId="grant-granted-scopes" />
                    </Group>
                  </>
                )}

                {showError && (
                  <SectionBannerRoot variant="critical" data-testid="grant-error-banner">
                    <SectionBannerIcon />
                    <SectionBannerContent>
                      <SectionBannerDescription>{COPY.errorGeneric}</SectionBannerDescription>
                    </SectionBannerContent>
                    <SectionBannerCloseButton onClick={() => setShowError(false)} />
                  </SectionBannerRoot>
                )}

                <Group variant="horizontal" spacing="xs" alignItems="center">
                  <Button
                    label={COPY.cancel}
                    variant="tertiary"
                    size="medium"
                    isFullWidth
                    onClick={() => handleDeny(interaction)}
                    data-testid="grant-cancel-button"
                  />
                  <Button
                    label={COPY.authorize}
                    variant="primary"
                    size="medium"
                    isFullWidth
                    isLoading={isSubmitting}
                    onClick={() => void handleApprove(interaction)}
                    data-testid="grant-allow-button"
                  />
                </Group>
              </Group>
            </Container>
          </Group>
        </Container>
      </Container>
    </Container>
  );
};
